import { Controller } from "@hotwired/stimulus"

/**
 * Automatically submits a form when inputs change.
 * Supports debouncing for text inputs and maintains focus/selection state across Turbo updates.
 */
export default class extends Controller {
  static targets = ["input"]

  static values = {
    // Delay in milliseconds before submitting after an input event.
    delay: { type: Number, default: 300 }
  }

  connect() {
    this.timeout = null
    this.restoreFocusFromStorage()
  }

  disconnect() {
    this.clearTimeout()
  }

  /**
   * Main entry point for the auto-submit action.
   * If triggered by an 'input' event, it debounces the submission.
   * Otherwise, it submits immediately.
   */
  submit(event) {
    if (event.type === "input") {
      this.rememberFocusState(event.target)
      this.scheduleSubmit()
      return
    }

    this.clearTimeout()
    this.element.requestSubmit()
  }

  /**
   * Schedules a form submission after the configured delay.
   */
  scheduleSubmit() {
    this.clearTimeout()
    this.timeout = window.setTimeout(() => {
      this.element.requestSubmit()
    }, this.delayValue)
  }

  /**
   * Cancels any pending scheduled submission.
   */
  clearTimeout() {
    if (this.timeout) {
      window.clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  /**
   * Saves the current cursor position/selection of an input to sessionStorage.
   * Used to restore focus after Turbo replaces the DOM.
   */
  rememberFocusState(input) {
    if (!this.hasInputTarget || !input || input !== this.inputTarget || document.activeElement !== input) return

    const storage = this.focusStorage()
    const key = this.storageKey()
    if (!key) return

    const selectionStart = input.selectionStart ?? input.value.length
    const selectionEnd = input.selectionEnd ?? input.value.length

    storage.setItem(key, JSON.stringify({
      selectionStart,
      selectionEnd
    }))
  }

  /**
   * Restores focus and cursor position to the input target if state was saved.
   */
  restoreFocusFromStorage() {
    if (!this.hasInputTarget) return

    const storage = this.focusStorage()
    const key = this.storageKey()
    if (!key) return

    const payload = storage.getItem(key)
    if (!payload) return

    storage.removeItem(key)

    let selection
    try {
      selection = JSON.parse(payload)
    } catch {
      return
    }

    requestAnimationFrame(() => {
      this.inputTarget.focus()

      if (typeof this.inputTarget.setSelectionRange === "function") {
        const start = Number.isInteger(selection.selectionStart) ? selection.selectionStart : this.inputTarget.value.length
        const end = Number.isInteger(selection.selectionEnd) ? selection.selectionEnd : this.inputTarget.value.length
        this.inputTarget.setSelectionRange(start, end)
      }
    })
  }

  /**
   * Generates a unique key for storing focus state based on action URL and input identifier.
   */
  storageKey() {
    if (!this.hasInputTarget) return null

    const identifier = this.inputTarget.id || this.inputTarget.name || "input"
    return `auto-submit:${this.element.action || window.location.pathname}:${identifier}`
  }

  focusStorage() {
    return window.sessionStorage
  }
}
